// ===== components/OffersTable.jsx =====
import { Img, Heading, Button } from "./..";
import React, { useMemo, useState } from "react";
import { API_DOMAIN } from "config";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { formatPrice } from "utils/formatPrice";

const COLS = [
  { key: "model", title: "דגם הרכב", w: "10%" },
  { key: "buyer", title: "חברה", w: "7%" },
  { key: "rate", title: "שנת ייצור", w: "8%" },
  { key: "date", title: "גיר", w: "10%" },
  { key: "city", title: "קילומטר", w: "10%" },
  { key: "reserved", title: "יד", w: "5%" },
  { key: "pickup", title: "תאריך", w: "15%" },
  { key: "payEnd", title: "הצעת מחיר", w: "9%" },
  { key: "price", title: "מיועד ל", w: "15%" },
  { key: "action", title: "סטטוס", w: "10%" },
];


const statusMap = {
  0: { txt: "ממתין להצעה",clr: "rgb(214, 31, 31)", bg: "rgb(255, 233, 233)"    },
  1: { txt: "ממתין לאישור", clr: "rgb(214, 31, 31)", bg: "rgb(255, 233, 233)" },
  2: { txt: "מאושר", clr: "rgb(119, 172, 59)", bg: "rgb(237, 255, 216)" },
  3: { txt: "הצעה שנדחתה", clr: "rgb(48, 49, 49)", bg: "rgb(233, 233, 233)" },
  4: { txt: "ארכיון", clr: "rgb(48, 49, 49)", bg: "rgb(233, 233, 233)" },
  5: { txt: "לא מעונין", clr: "rgb(48, 49, 49)", bg: "rgb(233, 233, 233)" },
  6: { txt: "העסקה נסגרה ברכישה מידית", clr: "rgb(48, 49, 49)", bg: "rgb(233, 233, 233)" },
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return "-";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = String(d.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
};

const toNumber = (v) => {
  if (v == null) return 0;
  const s = String(v).replace(/[^\d.-]/g, ""); // מוריד פסיקים/₪/טקסט
  const n = Number(s);
  return Number.isNaN(n) ? 0 : n;
};

const getDisplayStatus = (row) => {
  const raw = row?.offer_status ?? 0;
  // לוגיקה שלך: אם ממתין להצעה + approved_other => ארכיון (4)
  return raw === 0 && Number(row?.approved_other) === 1
    ? (Number(row?.immediate_purchase_closed ?? row?.immediatePurchaseClosed ?? 0) === 1 ? 6 : 4)
    : raw;
};

const getSortValue = (row, key) => {
  const displayStatus = getDisplayStatus(row);

  switch (key) {
    case "model":
      return row?.model ?? "";
    case "buyer":
      return row?.manufacturer ?? "";
    case "rate":
      return toNumber(row?.year);
    case "date":
      return row?.gearbox ?? "";
    case "city":
      return toNumber(row?.kilometer);
    case "reserved":
      return toNumber(row?.hand);
    case "pickup":
      return row?.creation_date ? new Date(row.creation_date).getTime() : 0;

    // הצעת מחיר: טווח -> ממיין לפי מינימום בטווח, אחרת offered_price, ואם ממתין להצעה -> asking_price
    case "payEnd": {
      if (displayStatus === 0) return toNumber(row?.asking_price);

      const range = row?.offer_range;
      if (range && String(range).includes("-")) {
        const [minPart] = String(range).split("-");
        return toNumber(minPart);
      }
      return toNumber(row?.offered_price);
    }

    // מיועד ל (פירוק/שיקום)
    case "price":
      return toNumber(row?.sale_type_id);

    // סטטוס
    case "action":
      return displayStatus;

    default:
      return row?.[key] ?? "";
  }
};

const compareValues = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;

  if (typeof a === "number" && typeof b === "number") return a - b;

  return String(a).localeCompare(String(b), "he", {
    numeric: true,
    sensitivity: "base",
  });
};

export default function OffersTable({
  cars,
  onOfferApproved,
  updateShowApprovedOfferPopup,
  setSelectedOffer,
  currentUser,
  onUpdate = () => {},
  setSelectedCar,
  onOfferClick,
}) {
  const navigate = useNavigate();

  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const template = useMemo(() => COLS.map((c) => c.w).join(" "), []);

  const sortedCars = useMemo(() => {
    const arr = Array.isArray(cars) ? [...cars] : [];
    if (!sortKey) return arr;

    const dirMul = sortDir === "asc" ? 1 : -1;

    // stable-ish sort (שומר סדר מקורי כששווה)
    return arr
      .map((row, idx) => ({ row, idx }))
      .sort((a, b) => {
        const av = getSortValue(a.row, sortKey);
        const bv = getSortValue(b.row, sortKey);
        const res = compareValues(av, bv);
        return res !== 0 ? dirMul * res : a.idx - b.idx;
      })
      .map((x) => x.row);
  }, [cars, sortKey, sortDir]);

  const setSort = (key, dir) => {
    setSortKey(key);
    setSortDir(dir);
  };

  const handleClickRow = (row) => {
    if (onOfferClick) return onOfferClick(row);
    navigate(`/dealer-cars/${row?.car_id ?? row?.id}`);
  };

  const approveOffer = async (offer, carRow) => {
    try {
      await axios.post(`${API_DOMAIN}/offer/approveOfferByProvider`, {
        offer_id: Number(offer.id),
      });

      onOfferApproved?.(carRow?.id ?? carRow?.car_id, offer.id);

      setSelectedOffer?.(offer);
      setSelectedCar?.(carRow);
      updateShowApprovedOfferPopup?.(true);
    } catch (err) {
      console.error(err);
      alert("אירעה שגיאה באישור ההצעה");
    }
  };

  const markNotInterested = async (carId) => {
    if (!currentUser?.id) return;
    try {
      await axios.post(`${API_DOMAIN}/car/markNotInterested`, {
        car_id: carId,
        user_id: currentUser.id,
      });
      onUpdate();
    } catch (err) {
      console.error(err);
      alert("שגיאה בסימון 'לא מעונין'");
    }
  };

  const removeNotInterested = async (carId) => {
    if (!currentUser?.id) return;
    try {
      await axios.post(`${API_DOMAIN}/car/removeNotInterested`, {
        car_id: carId,
        user_id: currentUser.id,
      });
      onUpdate();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div>
      <div className="mt-2 flex w-full flex-col gap-2.5 self-end">
        <div className="flex items-start gap-[38px] md:ml-0 md:flex-col">
          <div className="w-[100%]">
            <div className="flex justify-center md:mx-0 md:flex-col">
              <div dir="rtl" className="w-full overflow-x-auto">
                {/* HEADER */}
                <div
                  className="grid items-center bg-white sticky top-0 z-10"
                  style={{
                    gridTemplateColumns: template,
                    borderBottom: "1px solid #E9E9E9",
                  }}
                >
                  {COLS.map((c) => (
                    <div
                      key={c.key}
                      className="flex items-center justify-center py-2 text-sm font-assistant font-bold relative gap-2 select-none"
                    >
                      <span>{c.title}</span>

                      {/* sort arrows */}
                      <div style={{ width: "12px" }} className="flex flex-col items-center">
                        <img
                          src="/images/img_vector_red_a700_03_9.svg"
                          alt="up-arrow"
                          className="mb-0.5 cursor-pointer"
                          onClick={() => setSort(c.key, "asc")}
                          style={{
                            opacity: sortKey === c.key && sortDir === "asc" ? 1 : 0.35,
                          }}
                        />
                        <img
                          src="/images/img_vector_red_a700_03_10.svg"
                          alt="down-arrow"
                          className="mt-0.5 cursor-pointer"
                          onClick={() => setSort(c.key, "desc")}
                          style={{
                            opacity: sortKey === c.key && sortDir === "desc" ? 1 : 0.35,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* ROWS */}
                {sortedCars?.map((row, i) => {
                  const carId = row?.id ?? row?.car_id;

                  const displayStatus = getDisplayStatus(row);
                  const isWaitingForOffer = displayStatus === 0;

                  const offerText = row?.offer_range ?? row?.offered_price ?? null;

                  const { txt, clr, bg } = statusMap[displayStatus] || { txt: "", clr: "#666" };

                  return (
                    <div
                      key={carId ?? i}
                      className="grid items-center gap-0 text-[15px]"
                      style={{
                        gridTemplateColumns: template,
                        borderBottom: "1px solid #E9E9E9",
                        height: "4rem",
                        margin: "0.5rem 0",
                      }}
                      // אם תרצי שבלחיצה על כל שורה נפתח פירוט:
                      // onClick={() => handleClickRow(row)}
                    >
                      {/* דגם */}
                      <span className="flex justify-center items-center">
                        {row?.model || "-"}
                      </span>

                      {/* חברה */}
                      <span className="text-center relative">
                        {row?.manufacturer || "-"}
                        <div className="absolute right-0 top-0 bottom-0 w-px bg-gray-300" />
                      </span>

                      {/* שנה */}
                      <span className="flex justify-center relative">
                        {row?.year ?? "-"}
                        <div className="absolute right-0 top-0 bottom-0 w-px bg-gray-300" />
                      </span>

                      {/* גיר */}
                      <span className="text-center relative">
                        {row?.gearbox ?? "-"}
                        <div className="absolute right-0 top-0 bottom-0 w-px bg-gray-300" />
                      </span>

                      {/* קילומטר */}
                      <span className="text-center relative">
                        {row?.kilometer ?? "-"}
                        <div className="absolute right-0 top-0 bottom-0 w-px bg-gray-300" />
                      </span>

                      {/* יד */}
                      <span className="flex justify-center relative">
                        {row?.hand ?? "-"}
                        <div className="absolute right-0 top-0 bottom-0 w-px bg-gray-300" />
                      </span>

                      {/* תאריך */}
                      <span className="text-center relative">
                        {formatDate(row?.creation_date)}
                        <div className="absolute right-0 top-0 bottom-0 w-px bg-gray-300" />
                      </span>

                      {/* הצעת מחיר */}
                      {isWaitingForOffer ? (
                        <div
                          style={{
                            opacity: 0.85,
                            zIndex: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Heading
                            size="headingxl"
                            className="!font-heebo text-[14px] font-bold ml-2"
                            style={{ textAlign: "center", fontWeight: "400" }}
                          >
                          מחיר מומלץ: ₪{formatPrice(Number(row.asking_price?? "-"))}
                          </Heading>
                        </div>
                      ) : offerText ? (
                        <div style={{ opacity: 0.85, zIndex: 2 }}>
                          <Heading
                            size="headingxl"
                            className="!font-heebo text-[14px] ml-2"
                            style={{ textAlign: "center", fontWeight: "400" }}
                          >
                            {String(offerText).includes("-")
                              ? "טווח הצעות: " + offerText
                              : "מחיר ההצעה: ₪" + offerText}
                          </Heading>
                        </div>
                      ) : (
                        <div style={{ opacity: 0.85, zIndex: 2 }}>
                          <Heading
                            size="headingxl"
                            className="!font-heebo text-[14px] ml-2"
                            style={{ textAlign: "center" }}
                          >
                            --------
                          </Heading>
                        </div>
                      )}

                      {/* מיועד ל */}
                      <span className="flex items-center justify-center relative w-full">
                        <Button
                          shape="round"
                          className="min-w-[116px] gap-2 rounded-[14px] px-[18px] font-medium capitalize !text-black-900 flex items-center"
                        >
                          <Img
                            src={
                              Number(row?.sale_type_id) === 1
                                ? "/images/total.svg"
                                : "/images/img_television_red_a700_03_2.svg"
                            }
                            alt="SaleType"
                          />
                          {Number(row?.sale_type_id) === 1 ? "פירוק" : "שיקום"}
                        </Button>
                        <div className="absolute right-0 top-0 bottom-0 w-px bg-gray-300" />
                      </span>

                      {/* סטטוס */}
                      <span className="flex items-center justify-center relative w-full">
                        <Button
                          size="lg"
                          style={{
                            backgroundColor: bg,
                            color: clr,
                            fontSize: "12px",
                            height: "auto",
                          }}
                            className="px-3 py-1  text-[12px] font-semibold leading-none"
                        >
                          {txt}
                        </Button>
                      </span>

                      {/* כפתור ארכיון (לא מעונין) */}
                      <span className="flex justify-center">
                        <div className="flex items-center justify-between w-full">
                          {currentUser?.user_type === 3 && (
                            displayStatus === 0 ? (
                              <Button
                                size="lg"
                                shape="round"
                                onClick={() => markNotInterested(carId)}
                                style={{
                                  border: "1px solid #666",
                                  color: "#666",
                                  fontSize: "12px",
                                  height: "auto",
                                }}
                                className="w-[90px] py-1.5 gap-2 rounded-[16px] font-semibold"
                              >
                                העבר לארכיון
                              </Button>
                            ) : displayStatus === 5 ? (
                              <Button
                                size="lg"
                                shape="round"
                                onClick={() => removeNotInterested(carId)}
                                style={{
                                  border: "1px solid #666",
                                  color: "#666",
                                  fontSize: "12px",
                                  height: "auto",
                                }}
                                className="w-[90px] py-1.5 gap-2 rounded-[16px] font-semibold"
                              >
                                הסר מארכיון
                              </Button>
                            ) : (
                              <span style={{ width: 90 }} />
                            )
                          )}
                        </div>
                      </span>

                      {/* אם תרצי להחזיר “צפייה בהצעה”/אקשן, תגידי ואני מחזיר בעמודה ייעודית */}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
